import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import { toast } from 'sonner';
import type { AgencyMember, MemberSignupStatus } from '@/types';

interface MemberSignupStatusDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  member: AgencyMember | null;
}

function describeStatus(data: MemberSignupStatus): { label: string; heat: string; canResend: boolean } {
  if (data.enabled === false) {
    return { label: 'מושבת', heat: 'heat-lost', canResend: false };
  }
  switch (data.status) {
    case 'FORCE_CHANGE_PASSWORD':
      return { label: 'הזמנה נשלחה — טרם בוצעה כניסה ראשונה', heat: 'heat-warm', canResend: true };
    case 'CONFIRMED':
      return { label: 'פעיל (השלים הרשמה)', heat: 'heat-closing', canResend: false };
    case 'RESET_REQUIRED':
      return { label: 'נדרש איפוס סיסמה', heat: 'heat-hot', canResend: false };
    default:
      return { label: data.status || 'לא ידוע', heat: 'heat-cold', canResend: false };
  }
}

export function MemberSignupStatusDialog({ open, onClose, tenantId, member }: MemberSignupStatusDialogProps) {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['member-signup-status', tenantId, member?.user_id],
    queryFn: () => api.getMemberSignupStatus(tenantId, member!.user_id),
    enabled: open && !!member,
  });

  const resendMutation = useMutation({
    mutationFn: () => api.resendMemberInvite(tenantId, member!.user_id),
    onSuccess: () => {
      toast.success('ההזמנה נשלחה מחדש');
      queryClient.invalidateQueries({ queryKey: ['member-signup-status', tenantId, member?.user_id] });
    },
    onError: (err: Error) => toast.error('שליחת ההזמנה נכשלה', { description: err.message }),
  });

  const info = data ? describeStatus(data) : null;

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>סטטוס הזמנת משתמש</h2>
          <button className="modal-close" onClick={onClose} aria-label="סגור">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="modal-body">
          <p className="import-intro" style={{ marginBottom: 14 }}>{member?.name || member?.email || member?.username}</p>

          {isLoading ? (
            <div className="empty">טוען…</div>
          ) : isError ? (
            <div className="lead-note warn">שגיאה בטעינת הסטטוס</div>
          ) : data && info ? (
            <>
              <div className="field" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="lead-field-label">סטטוס</span>
                <span className={`stage-badge ${info.heat}`}>{info.label}</span>
              </div>

              <div className="harb-file" style={{ marginTop: 12 }}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" />
                </svg>
                <div className="lead-cell" style={{ gap: 2 }}>
                  <span className="cell-microlabel" style={{ display: 'block' }}>כתובת שאליה נשלחה ההזמנה</span>
                  <span className="cell-value strong" dir="ltr">{data.username || '—'}</span>
                </div>
              </div>

              {info.canResend && (
                <p className="import-ignored" style={{ marginTop: 12 }}>
                  המשתמש טרם נכנס למערכת. ניתן לשלוח את ההזמנה מחדש לכתובת המוצגת מעלה.
                </p>
              )}
            </>
          ) : null}
        </div>

        <div className="modal-footer">
          <button className="btn outline" onClick={onClose}>סגור</button>
          <div className="spacer" />
          {info?.canResend && (
            <button className="btn" onClick={() => resendMutation.mutate()} disabled={resendMutation.isPending}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
              {resendMutation.isPending ? 'שולח…' : 'שלח הזמנה מחדש'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
